import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, ClipboardList, Loader2, Mail } from 'lucide-react'

import { apiFetch } from '../utils/api'

type CandidateState = 'VERIFICADO' | 'CONDICIONADO' | 'ESTIMADO'
  | 'VERIFIED' | 'CONDITIONAL' | 'ESTIMATED'

interface CandidateLine {
  itemId: string
  partida: string
  description: string
  quantityBaseWithoutWaste: number
  wastePct: number
  quantity: number
  unit: string
  state: CandidateState
  provenance: Record<string, unknown>
  provenanceLabel: string
  notSendable: boolean
  sourceSha256: string
  runId: string
}

interface CandidatePackage {
  packageId: string
  packageName: string
  counts: { verified: number; conditioned: number; estimated: number }
  lines: CandidateLine[]
}

export interface CandidateResponse {
  contractVersion: string
  version: string
  sourceSha256: string
  runId: string
  lineCount: number
  createdAt: string
  packages: CandidatePackage[]
}

export type CandidateLoadResult =
  | { kind: 'ready'; data: CandidateResponse }
  | { kind: 'missing' }

export async function parseCertifiedPurchaseCandidateResponse(
  response: Response,
): Promise<CandidateLoadResult> {
  if (response.status === 404) return { kind: 'missing' }
  if (!response.ok) throw new Error(`HTTP_${response.status}`)
  return { kind: 'ready', data: await response.json() as CandidateResponse }
}

interface CertifiedPurchaseCandidateContentProps {
  data: CandidateResponse | null
  isEs: boolean
  selectedLineKeys?: ReadonlySet<string>
  onToggleLine?: (line: CandidateLine) => void
  onTogglePackage?: (group: CandidatePackage) => void
  onPrepareSelection?: () => void
  isPreparingSelection?: boolean
}

function lineKey(line: Pick<CandidateLine, 'partida' | 'itemId'>): string {
  return `${line.partida}\u0000${line.itemId}`
}

const stateStyles: Record<CandidateState, string> = {
  VERIFICADO: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  VERIFIED: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  CONDICIONADO: 'border-amber-200 bg-amber-50 text-amber-900',
  CONDITIONAL: 'border-amber-200 bg-amber-50 text-amber-900',
  ESTIMADO: 'border-slate-300 bg-slate-100 text-slate-800',
  ESTIMATED: 'border-slate-300 bg-slate-100 text-slate-800',
}

function visibleProvenance(
  line: Pick<CandidateLine, 'provenanceLabel' | 'notSendable'>,
  isEs: boolean,
): { label: string; notSendable: boolean } {
  const label = typeof line.provenanceLabel === 'string' ? line.provenanceLabel.trim() : ''
  if (line.notSendable !== false || !label) {
    return {
      label: isEs ? 'Sin fuente — no enviable' : 'No source — not sendable',
      notSendable: true,
    }
  }
  return { label, notSendable: false }
}

function formatQuantity(value: number, language: string): string {
  return new Intl.NumberFormat(language === 'es' ? 'es' : 'en-US', {
    maximumFractionDigits: 4,
  }).format(value)
}

export function CertifiedPurchaseCandidateContent({
  data,
  isEs,
  selectedLineKeys = new Set(),
  onToggleLine,
  onTogglePackage,
  onPrepareSelection,
  isPreparingSelection = false,
}: CertifiedPurchaseCandidateContentProps) {
  if (data === null) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6" data-testid="purchase-candidate-empty">
        <div className="rounded-2xl border border-[#D8E6F4] bg-[#F7FAFF] p-5">
          <h1 className="text-xl font-black text-[#1C1A17]">
            {isEs ? 'Vista de compra candidata' : 'Purchase candidate view'}
          </h1>
          <p className="mt-2 text-sm font-semibold text-[#6B655C]">
            {isEs
              ? 'Este proyecto todavía no tiene una copia certificada para consulta.'
              : 'This project does not have a certified copy to review yet.'}
          </p>
        </div>
      </div>
    )
  }

  const language = isEs ? 'es' : 'en'
  const selectedCount = [...selectedLineKeys].length
  return (
    <main className="mx-auto max-w-7xl space-y-5 p-3 sm:p-6" data-testid="certified-purchase-candidate-view">
      <header className="rounded-2xl border border-[#D8E6F4] bg-[#F7FAFF] p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <ClipboardList className="mt-0.5 h-6 w-6 shrink-0 text-[#06539B]" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-black text-[#1C1A17] sm:text-2xl">
              {isEs ? 'Vista de compra candidata' : 'Purchase candidate view'}
            </h1>
            <p className="mt-1 text-sm font-semibold text-[#4E4942]">
              {isEs
                ? 'Copia de solo lectura de la cuantificación certificada. No envía solicitudes, no cotiza y no crea órdenes.'
                : 'Read-only copy of the certified takeoff. It sends no requests, prices nothing, and creates no orders.'}
            </p>
          </div>
        </div>
        <dl className="mt-4 grid gap-3 text-xs sm:grid-cols-3">
          <div><dt className="font-bold text-[#6B655C]">{isEs ? 'Versión' : 'Version'}</dt><dd className="mt-1 break-all font-semibold text-[#1C1A17]">{data.version}</dd></div>
          <div><dt className="font-bold text-[#6B655C]">{isEs ? 'Renglones' : 'Lines'}</dt><dd className="mt-1 font-semibold text-[#1C1A17]">{data.lineCount}</dd></div>
          <div><dt className="font-bold text-[#6B655C]">SHA-256</dt><dd className="mt-1 break-all font-mono text-[10px] text-[#1C1A17]">{data.sourceSha256}</dd></div>
        </dl>
      </header>

      <section className="rounded-2xl border border-[#D8E6F4] bg-[#F7FAFF] p-4 sm:p-5" data-testid="certified-selection-control">
        <h2 className="text-base font-black text-[#1C1A17]">{isEs ? 'Seleccionar partidas para borrador' : 'Select items for a draft'}</h2>
        <p className="mt-1 text-sm font-semibold text-[#4E4942]">
          {isEs
            ? 'Seleccione paquetes completos o renglones individuales. El borrador se forma únicamente desde la copia certificada; no se envía en esta pantalla.'
            : 'Select whole packages or individual lines. The draft is built only from the certified copy; this screen does not send it.'}
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <strong className="text-sm text-[#1C1A17]" data-testid="certified-selection-count">
            {isEs ? `${selectedCount} renglones seleccionados` : `${selectedCount} selected lines`}
          </strong>
          <button type="button" onClick={onPrepareSelection} disabled={selectedCount === 0 || isPreparingSelection} className="min-h-11 rounded-xl bg-[#06539B] px-4 py-2 text-sm font-black text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06539B] disabled:cursor-not-allowed disabled:opacity-50" data-testid="certified-prepare-selected-draft">
            <Mail className="mr-2 inline h-4 w-4" aria-hidden="true" />
            {isEs ? 'Armar borrador para revisión' : 'Build draft for review'}
          </button>
        </div>
      </section>

      {data.packages.map((group) => (
        <section key={group.packageId} className="overflow-hidden rounded-2xl border border-[#ECEAE4] bg-white" data-testid="purchase-candidate-package">
          <div className="border-b border-[#ECEAE4] bg-[#F8F7F4] px-4 py-3 sm:px-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-base font-black text-[#1C1A17]">{group.packageName}</h2>
                <p className="mt-1 text-xs font-bold text-[#6B655C]">
                  {group.counts.verified} {isEs ? 'verificados' : 'verified'} ·{' '}
                  {group.counts.conditioned} {isEs ? 'condicionados' : 'conditional'} ·{' '}
                  {group.counts.estimated} {isEs ? 'estimados' : 'estimated'}
                </p>
              </div>
              <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-[#06539B] bg-white px-4 py-2 text-sm font-extrabold text-[#06539B] focus-within:ring-2 focus-within:ring-[#06539B]">
                <input type="checkbox" checked={group.lines.length > 0 && group.lines.every((line) => selectedLineKeys.has(lineKey(line)))} onChange={() => onTogglePackage?.(group)} className="h-4 w-4 accent-[#06539B]" data-testid={`certified-select-package-${group.packageId}`} />
                {isEs ? 'Seleccionar paquete' : 'Select package'}
              </label>
            </div>
          </div>
          <div className="divide-y divide-[#ECEAE4]">
            {group.lines.map((line) => {
              const provenance = visibleProvenance(line, isEs)
              return (
              <article key={`${line.partida}:${line.itemId}`} className={`grid gap-3 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_160px_150px] sm:px-5 ${provenance.notSendable ? 'bg-red-50/60' : ''}`} data-testid="purchase-candidate-line" data-not-sendable={provenance.notSendable ? 'true' : 'false'}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="checkbox" checked={selectedLineKeys.has(lineKey(line))} onChange={() => onToggleLine?.(line)} className="h-4 w-4 shrink-0 accent-[#06539B]" aria-label={isEs ? `Seleccionar ${line.itemId}` : `Select ${line.itemId}`} data-testid="certified-select-line" />
                    <span className={`rounded-full border px-2 py-1 text-[10px] font-black tracking-wide ${stateStyles[line.state]}`}>{line.state}</span>
                    <span className="break-all font-mono text-[10px] font-semibold text-[#6B655C]">{line.partida} · {line.itemId}</span>
                  </div>
                  <h3 className="mt-2 text-sm font-extrabold text-[#1C1A17]">{line.description}</h3>
                  <p className={`mt-1 flex items-start gap-1.5 text-xs font-semibold ${provenance.notSendable ? 'text-red-800' : 'text-[#6B655C]'}`}>
                    {provenance.notSendable && <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                    <span>{provenance.label}</span>
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-[#6B655C]">{isEs ? 'Cantidad base' : 'Base quantity'}</p>
                  <p className="mt-1 text-sm font-extrabold text-[#1C1A17]">{formatQuantity(line.quantityBaseWithoutWaste, language)} {line.unit}</p>
                  <p className="mt-1 text-xs font-semibold text-[#6B655C]">{isEs ? 'Merma' : 'Waste'}: {formatQuantity(line.wastePct, language)}%</p>
                </div>
                <div className="rounded-xl bg-[#F7FAFF] p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-[#4E4942]">{isEs ? 'Cantidad candidata' : 'Candidate quantity'}</p>
                  <p className="mt-1 text-base font-black text-[#06539B]">{formatQuantity(line.quantity, language)} {line.unit}</p>
                </div>
              </article>
              )
            })}
          </div>
        </section>
      ))}

      <footer className="flex items-start gap-2 rounded-xl border border-[#D8E6F4] bg-white p-4 text-xs font-semibold text-[#4E4942]">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-[#06539B]" aria-hidden="true" />
        <span>{isEs ? 'Identidad conservada por partida + item_id. Esta pantalla es de consulta.' : 'Identity is preserved by trade + item_id. This screen is for review only.'}</span>
      </footer>
    </main>
  )
}

export default function CertifiedPurchaseCandidateView() {
  const { projectId } = useParams<{ projectId: string }>()
  const { getToken } = useAuth()
  const { i18n } = useTranslation()
  const isEs = i18n.language.startsWith('es')
  const language = isEs ? 'es' : 'en'
  const navigate = useNavigate()
  const [selectedLineKeys, setSelectedLineKeys] = useState<Set<string>>(new Set())

  const query = useQuery<CandidateLoadResult>({
    queryKey: ['certified-purchase-candidates', projectId, language],
    enabled: Boolean(projectId),
    queryFn: async () => {
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates?lang=${language}`,
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      )
      return parseCertifiedPurchaseCandidateResponse(response)
    },
  })

  const selectionDraft = useMutation({
    mutationFn: async () => {
      const ready = query.data
      if (ready?.kind !== 'ready' || !projectId) throw new Error('NO_CERTIFIED_SELECTION')
      const selected = ready.data.packages.flatMap((group) => group.lines)
        .filter((line) => selectedLineKeys.has(lineKey(line)))
        .map((line) => ({ partida: line.partida, itemId: line.itemId }))
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates/selection-drafts?lang=${language}`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ lines: selected }),
        },
      )
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
      return response.json() as Promise<{ id: string }>
    },
    onSuccess: (draft) => navigate(`/projects/${projectId}/purchase-candidates/selection-drafts/${draft.id}?lang=${language}`),
  })

  if (query.isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center" data-testid="purchase-candidate-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#06539B]" aria-hidden="true" />
        <span className="ml-3 text-sm font-semibold text-[#6B655C]">
          {isEs ? 'Cargando líneas certificadas…' : 'Loading certified lines…'}
        </span>
      </div>
    )
  }

  if (query.data?.kind === 'missing') {
    return <CertifiedPurchaseCandidateContent data={null} isEs={isEs} />
  }

  if (query.isError || !query.data) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6" data-testid="purchase-candidate-error">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
          <AlertTriangle className="mb-3 h-6 w-6 text-amber-800" aria-hidden="true" />
          <h1 className="text-xl font-black text-[#1C1A17]">
            {isEs ? 'Vista de compra candidata' : 'Purchase candidate view'}
          </h1>
          <p className="mt-2 text-sm font-semibold text-[#6B655C]">
            {isEs
              ? 'No se pudieron cargar las líneas certificadas.'
              : 'The certified lines could not be loaded.'}
          </p>
        </div>
      </div>
    )
  }

  return <CertifiedPurchaseCandidateContent
    data={query.data.data}
    isEs={isEs}
    selectedLineKeys={selectedLineKeys}
    onToggleLine={(line) => setSelectedLineKeys((current) => {
      const next = new Set(current)
      const key = lineKey(line)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })}
    onTogglePackage={(group) => setSelectedLineKeys((current) => {
      const next = new Set(current)
      const allSelected = group.lines.every((line) => next.has(lineKey(line)))
      for (const line of group.lines) {
        if (allSelected) next.delete(lineKey(line))
        else next.add(lineKey(line))
      }
      return next
    })}
    onPrepareSelection={() => selectionDraft.mutate()}
    isPreparingSelection={selectionDraft.isPending}
  />
}
